import React, { useEffect, useState } from "react";

// region External libraries
import { SetStateAction, useAtom, WritableAtom } from "jotai";
import _ from "lodash";
import { Add } from "@mui/icons-material";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion External libraries
// region Helpers
import { EPermissionGroupModule, permissionGroupObject } from "src/helpers/permissionGroup";
// endregion Helpers
// region Languages
import { GlobalMessages, RoleRegisterMessages } from "@languages/interfaces";
import { useTranslation } from "react-i18next";
// endregion Languages
// region Atoms
import TabPanel from "@atoms/TabPanel";
import Loading from "@atoms/Loading";
import PageTitle from "@atoms/PageTitle";
import { selectedModuleIdAtom } from "src/atoms/SelectIdentityModule";
import AccessDenied from "@atoms/AccessDenied";
// endregion Atoms
// region Molecules
import FormRole from "src/molecules/FormRole";
import UserSelectionList from "@molecules/UserSelectionList";
// endregion Molecules
// region Organisms
import StepTabs, { IStepTabProps } from "@organisms/StepTabs";
import { useTabsAtom } from "@organisms/PageTabs";
import PermissionGroup, { EPermissionGroupOrigin } from "src/organisms/PermissionGroup";
// endregion Organisms
// region Hooks
import { useIdentity, PERMISSIONS } from "@hooks/useIdentity";
// endregion Hooks
// region Pages
import { selectedTabAtom, userRightsTabsAtom } from "@pages/UsersRights";
// endregion Pages
// region Store
import { roleById, TRoleByIdQueries } from "src/store/graphql/groups/role/query.graphql";
import {
  ECommandResultStatus,
  ESort,
  EWhereClausesOperators,
  PermissionGroupQueriesGetAllWithSortedPaginationArgs,
  PermissionGroupType,
  RoleClaimMutationsSaveRoleClaimsArgs,
  RoleClaimType,
  RoleMutationsDeleteArgs,
  RoleQueriesGetByIdArgs,
  RoleType
} from "src/store/graphql/helpers/types.graphql";
import { roleDelete, TRoleDeleteMutations } from "src/store/graphql/groups/role/mutation.graphql";
import {
  permissionGroupAllWithSortedPagination,
  TPermissionGroupAllWithSortedPaginationQueries
} from "src/store/graphql/groups/permissionGroup/query.graphql";
import { saveRoleClaims, TSaveRoleClaimsMutations } from "src/store/graphql/groups/roleClaim/mutation.graphql";
// endregion Store
// region Styles
import * as Styled from "./styles";

// endregion Styles

interface IRoleProps {
  selectedStepTabAtom: WritableAtom<number, SetStateAction<number>, void>;
  mode: "CREATE" | "EDIT";
  currentRoleId?: string;
}

enum EStepTabPanelIndexes {
  formRole = 0,
  addUsers = 1,
  permissionGroupsStepTabs = 2
}

const Role: React.FC<IRoleProps> = ({ selectedStepTabAtom, mode, currentRoleId }) => {
  // region Queries
  const [QueryGetRoleById] = useLazyQuery<TRoleByIdQueries, RoleQueriesGetByIdArgs>(
    roleById([
      "id", "name", "description", "moduleId", "userRoles.userId", "roleClaims.claimId"
    ])
  );
  const [QueryGetPermissionGroupAllWithPagination] = useLazyQuery<TPermissionGroupAllWithSortedPaginationQueries,
    PermissionGroupQueriesGetAllWithSortedPaginationArgs>(
      permissionGroupAllWithSortedPagination([
        "id", "alias"
      ])
    );
  // endregion Queries
  // region Mutation
  const [MutationUpdateRoleRelations] = useMutation<TSaveRoleClaimsMutations,
    RoleClaimMutationsSaveRoleClaimsArgs>(saveRoleClaims());
  const [MutationDeleteRole] = useMutation<TRoleDeleteMutations, RoleMutationsDeleteArgs>(
    roleDelete()
  );
  // endregion Mutation
  // region Hooks
  const {
    pageTabs,
    setPageTabs,
    selectedTab,
    setSelectedTab
  } = useTabsAtom(userRightsTabsAtom, selectedTabAtom);
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [selectedModuleId] = useAtom(selectedModuleIdAtom);
  const { validateUserAccess } = useIdentity();
  // endregion Hooks

  // region Constants
  const initialSteps: IStepTabProps[] = [
    {
      id: "stepsRoleRegister_roleTab",
      tabLabel: RoleRegisterMessages.stepTabFormRole
    },
    {
      id: "stepsRoleRegister_addUsersTab",
      tabLabel: RoleRegisterMessages.stepTabAddUsers
    }
  ];
  // endregion Constants

  // region States
  const [isLoading, setIsLoading] = useState(false);
  const [currentRole, setCurrentRole] = useState<RoleType>({} as RoleType);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroupType[]>([]);
  const [selectedStepTab, setSelectedStepTab] = useAtom(selectedStepTabAtom);
  const [currentSteps, setCurrentSteps] = useState<IStepTabProps[]>(initialSteps);
  // endregion States

  // region Constants
  const title = `${t(mode === "CREATE" ? RoleRegisterMessages.titleCreate : RoleRegisterMessages.titleEdit)}
    ${!_.isEmpty(currentRole) ? ` - ${currentRole.name}` : ""}`;
  // endregion Constants

  // region Functions
  const updateSelectedClaims = (claimIds: string[]) => {
    const updatedRoleClaims = claimIds.map((claimId) => ({ claimId } as RoleClaimType));

    setCurrentRole((value) => ({ ...value, roleClaims: updatedRoleClaims }));
  };

  const getPermissionGroups = async () => {
    try {
      const query = await QueryGetPermissionGroupAllWithPagination({
        variables: {
          where: [
            {
              property: "moduleId",
              operator: EWhereClausesOperators.EQUALS,
              value: selectedModuleId
            }
          ],
          sort: [
            {
              field: "order",
              sortDirection: ESort.ASC
            }
          ]
        }
      });

      const response = query.data?.permissionGroup.getAllWithSortedPagination;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as PermissionGroupType[];

      return response.result as PermissionGroupType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as PermissionGroupType[];
    }
  };

  const getCurrentRole = async (roleId: string) => {
    try {
      const query = await QueryGetRoleById({ variables: { id: roleId } });

      const response = query.data?.role.getByID;

      if (response?.status !== ECommandResultStatus.SUCCESS) return {} as RoleType;

      return response.result as RoleType;
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return {} as RoleType;
    }
  };
  // region Functions

  // region Handlers
  const onSaveRoleForm = async (data: RoleType) => {
    const updatedTabs = [...pageTabs];

    updatedTabs[selectedTab].id = data.id;

    setPageTabs(updatedTabs);
    setCurrentRole(_.merge(currentRole, data));
    setSelectedStepTab((value) => value + 1);
  };

  const onDeleteRoleForm = async () => {
    try {
      setIsLoading(true);

      const mutation = await MutationDeleteRole({ variables: { id: currentRole.id } });

      const response = mutation.data?.role.delete;

      if (response?.status !== ECommandResultStatus.SUCCESS) return;

      addToast({ type: "success", title: response.message });

      const updatedTabs = pageTabs.filter((_value, index) => index !== selectedTab);

      setPageTabs(updatedTabs);
      setSelectedTab(0);
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndOfProcess = async () => {
    const updateRole = async () => {
      const mutation = await MutationUpdateRoleRelations({
        variables: {
          saveRoleClaimsInput: {
            roleUpdateInput: {
              id: currentRole.id,
              moduleId: currentRole.moduleId
            },
            users: currentRole.userRoles?.map((userRole) => userRole?.userId) ?? [],
            claims: currentRole.roleClaims?.map((roleClaim) => roleClaim?.claimId) ?? []
          }
        }
      });

      return mutation.data?.roleClaim.saveRoleClaims;
    };

    try {
      setIsLoading(true);

      const response = await updateRole();

      if (response?.status !== ECommandResultStatus.SUCCESS) return;

      addToast({ type: "success", title: response.message });

    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });
    } finally {
      setIsLoading(false);
    }

    const updatedTabs = pageTabs.filter((_value, index) => index !== selectedTab);

    setSelectedTab(0);
    setPageTabs(updatedTabs);
  };
  // endregion Handlers

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const permissionGroups = await getPermissionGroups();

      if (currentRoleId) {
        const role = await getCurrentRole(currentRoleId);

        setCurrentRole(role);
      }

      const permissionSteps = permissionGroups.map((permissionGroup) => ({
        id: permissionGroup.id,
        tabLabel: permissionGroupObject[permissionGroup.alias as EPermissionGroupModule].title
      } as IStepTabProps));

      setPermissionGroups(permissionGroups);
      setCurrentSteps([...currentSteps, ...permissionSteps]);
      setIsLoading(false);
    })();

    return () => {
      setCurrentRole({} as RoleType);
      setPermissionGroups([]);
    };
  }, []);

  const hasPermission = mode === "CREATE"
    ? validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.GROUPS.INCLUDE)
    : validateUserAccess(PERMISSIONS.USER_RIGHTS_CONTROL.GROUPS.ALTER);

  if (!hasPermission) {
    return <AccessDenied />;
  }

  return (
    <Styled.Container>
      <Loading loading={isLoading} />
      <PageTitle>{title}</PageTitle>
      <StepTabs
        content={currentSteps}
        selectedStepTab={selectedStepTab}
        setSelectedStepTab={setSelectedStepTab}
        mode={mode}
        disableControlButtons={mode === "CREATE" && _.isEmpty(currentRole)}
        // empty function because there is no finish process button on the final step
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onFinish={() => {}}
        showConfirmButtonOnLastStepTab={false}
        additionalControlButtons={
         [{
           id: "saveButton",
           displayFromStepTab: 2,
           extended: true,
           onClick: () => handleEndOfProcess(),
           content: (
             <Styled.SaveRoleButton>
               {t(GlobalMessages.saveText)}
               <Add fontSize="large" />
             </Styled.SaveRoleButton>
           )
         }]
        }
      >
        <TabPanel
          id="full-width-formRoleStepTabPanel"
          value={selectedStepTab}
          index={EStepTabPanelIndexes.formRole}
        >
          <FormRole onSave={onSaveRoleForm} onDelete={onDeleteRoleForm} currentRole={currentRole} />
        </TabPanel>
        <TabPanel
          id="full-width-addUsersStepTabPanel"
          value={selectedStepTab}
          index={EStepTabPanelIndexes.addUsers}
          unmountOnExit
        >
          <UserSelectionList
            currentRole={currentRole}
            onUsersChange={(userIds) => setCurrentRole((value) => ({ ...value, userRoles: userIds }))}
          />
        </TabPanel>
        {permissionGroups.map((permissionGroup, index) => (
          <TabPanel
            key={permissionGroup.id}
            id={`full-width-${permissionGroup.name}StepTabPanel`}
            value={selectedStepTab}
            index={EStepTabPanelIndexes.permissionGroupsStepTabs + index}
            unmountOnExit
          >
            <PermissionGroup
              permissionGroupId={permissionGroup.id}
              origin={EPermissionGroupOrigin.ROLE}
              onClaimGroupsChange={updateSelectedClaims}
              selectedClaims={(currentRole.roleClaims ?? []) as RoleClaimType[]}
            />
          </TabPanel>
        ))}
      </StepTabs>
    </Styled.Container>
  );
};

Role.defaultProps = {
  currentRoleId: undefined
};

export default Role;